import { z } from 'zod';
import { ProjectAssignmentStatusSchema, ProjectStatusSchema, ProjectWorkItemStatusSchema, ProjectWorkLogAuthorableKindSchema, ProjectWorkLogKindSchema, } from '../lib/enums';
import { zDateOnly, zIsoDateTime, zUuid } from './common';
/**
 * Projects — execution tracking for work already won (bible §6.2, roadmap
 * P9.1/P9.2). The THIRD sanctioned cross-tenant crossing, built on the same
 * doctrine as the sharing seam.
 *
 * NAMING: `ProjectSite*` in ./clients is a CLIENT ADDRESS record and has
 * NOTHING to do with these shapes. Nothing here is named `*Site*` as a noun;
 * the only location value a subcontractor ever sees is `siteLabel: string`,
 * which is COPIED text (exactly like `scopeDescription` on a scope package).
 *
 * Structural laws these shapes enforce:
 *  - The SUB-facing shapes (`SubProject*`) are declared LITERALLY, never
 *    derived from the contractor shapes via .omit()/.pick(). A new field on a
 *    contractor DTO must therefore be added to the sub DTO ON PURPOSE — it can
 *    never leak in by inheritance. Every request shape is .strict().
 *  - No sub-facing shape has a key for a client, an estimate, a price, another
 *    subcontractor, or a user identity. There is nothing to filter out,
 *    because there is no field to put it in.
 *  - Progress percentages are DERIVED from item counts (lib/progress), never
 *    stored, and never accepted from a client as authority.
 *  - Work logs are append-only. A mistake is fixed by appending a `correction`
 *    carrying `correctsLogId`; nothing is ever edited or deleted.
 *  - Money appears nowhere in this file, by design.
 */
// ------------------------------------------------------------------- progress
/**
 * Item counts for one assignment or a whole project. `percentComplete` is
 * null when there is no checklist yet — "no checklist" and "0% done" are
 * different facts and must not render the same.
 */
export const ProgressSummarySchema = z
    .object({
    itemsTotal: z.number().int().min(0),
    itemsDone: z.number().int().min(0),
    itemsBlocked: z.number().int().min(0),
    itemsRemaining: z.number().int().min(0),
    percentComplete: z.number().int().min(0).max(100).nullable(),
})
    .strict();
/** Project-wide roll-up: item counts plus how the crews are tracking. */
export const ProjectProgressSchema = ProgressSummarySchema.extend({
    assignmentsTotal: z.number().int().min(0),
    /** Crews that declared their portion finished (`complete` or `accepted`). */
    assignmentsComplete: z.number().int().min(0),
    /** Crews with no work items yet — excluded from the percentage above. */
    assignmentsWithoutChecklist: z.number().int().min(0),
    /** Most recent field activity anywhere on the project. */
    lastActivityAt: zIsoDateTime.nullable(),
}).strict();
// ------------------------------------------------------------------- projects
export const ProjectSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(300),
    /** Per-tenant document number, e.g. `PRJ-0007`. */
    number: z.string().max(40).nullable(),
    status: ProjectStatusSchema,
    /** COPIED location text — the only location string that crosses the seam. */
    siteLabel: z.string().max(400),
    clientId: zUuid.nullable(),
    clientName: z.string().max(200).nullable(),
    /** The CRM address record this was created from (see ./clients). */
    siteId: zUuid.nullable(),
    estimateId: zUuid.nullable(),
    estimateNumber: z.string().max(40).nullable(),
    startsOn: zDateOnly.nullable(),
    targetEndOn: zDateOnly.nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
/** List row: the project plus its derived roll-up. */
export const ProjectSummarySchema = ProjectSchema.extend({
    progress: ProjectProgressSchema,
});
export const CreateProjectRequestSchema = z
    .object({
    name: z.string().min(1).max(300),
    clientId: zUuid.nullable().optional(),
    /** A `project_sites` row belonging to the same client; copies siteLabel. */
    siteId: zUuid.nullable().optional(),
    siteLabel: z.string().max(400).optional(),
    startsOn: zDateOnly.nullable().optional(),
    targetEndOn: zDateOnly.nullable().optional(),
    /** Create an assignment for the contractor's own crew straight away. */
    includeOwnCrew: z.boolean().optional(),
})
    .strict();
/**
 * Scaffolding source for the initial checklist. `sections` is the default and
 * the only sensible one: estimate LINES are cost objects ("2x4 SPF #2, 8ft —
 * 340 lf") and scaffolding them yields a lumber list, not a punch list.
 */
export const ProjectScaffoldSourceSchema = z.enum(['sections', 'lines', 'none']);
export const CreateProjectFromEstimateRequestSchema = z
    .object({
    /** Defaults to the estimate's title. */
    name: z.string().min(1).max(300).optional(),
    siteLabel: z.string().max(400).optional(),
    startsOn: zDateOnly.nullable().optional(),
    targetEndOn: zDateOnly.nullable().optional(),
    /** Add an assignment for every sub whose quote was accepted (default true). */
    includeAcceptedSubs: z.boolean().optional(),
    includeOwnCrew: z.boolean().optional(),
    scaffoldFrom: ProjectScaffoldSourceSchema.optional(),
})
    .strict();
export const UpdateProjectRequestSchema = z
    .object({
    name: z.string().min(1).max(300).optional(),
    siteLabel: z.string().max(400).optional(),
    status: ProjectStatusSchema.optional(),
    startsOn: zDateOnly.nullable().optional(),
    targetEndOn: zDateOnly.nullable().optional(),
})
    .strict();
export const ProjectListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    status: ProjectStatusSchema.optional(),
});
// ---------------------------------------------------------------- assignments
/**
 * One participating company. `companyId === null` means the contractor's own
 * crew — first-class so the progress report covers the whole job, with zero
 * cross-tenant surface.
 */
export const ProjectAssignmentSchema = z.object({
    id: zUuid,
    projectId: zUuid,
    companyId: zUuid.nullable(),
    /** Via the company_directory barrier view; null for own crew. */
    companyName: z.string().max(200).nullable(),
    isOwnCrew: z.boolean(),
    tradeLabel: z.string().max(200),
    scopeSummary: z.string().max(50000),
    status: ProjectAssignmentStatusSchema,
    /** Provenance when the sub came in from an accepted sub-quote. */
    sourceSubQuoteId: zUuid.nullable(),
    /** When the sub declared its portion finished. */
    submittedAt: zIsoDateTime.nullable(),
    /** When the contractor signed the portion off (checklist freezes). */
    acceptedAt: zIsoDateTime.nullable(),
    progress: ProgressSummarySchema,
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateProjectAssignmentRequestSchema = z
    .object({
    /** null = the contractor's own crew. Otherwise must be in your directory. */
    companyId: zUuid.nullable(),
    tradeLabel: z.string().max(200).optional(),
    scopeSummary: z.string().max(50000).optional(),
    /** Optional provenance: the accepted sub-quote this came from. */
    sourceSubQuoteId: zUuid.nullable().optional(),
})
    .strict();
export const UpdateProjectAssignmentRequestSchema = z
    .object({
    tradeLabel: z.string().max(200).optional(),
    scopeSummary: z.string().max(50000).optional(),
})
    .strict();
export const AcceptPortionRequestSchema = z
    .object({ note: z.string().max(4000).optional() })
    .strict();
/** Sending work back needs a reason — it lands in the log the sub reads. */
export const ReopenPortionRequestSchema = z
    .object({ reason: z.string().min(1).max(4000) })
    .strict();
export const RemoveAssignmentRequestSchema = z
    .object({ reason: z.string().max(4000).optional() })
    .strict();
/**
 * A company the contractor may add. Sourced from accepted sub-quotes on the
 * linked estimate, or from `company_directory` (which crosses tenants only on
 * an existing relationship — there is no "search all companies").
 */
export const ProjectSubCandidateSchema = z.object({
    companyId: zUuid,
    companyName: z.string().max(200),
    trades: z.array(z.string().max(64)),
    source: z.enum(['accepted_quote', 'directory']),
    /** Set when `source === 'accepted_quote'`. */
    subQuoteId: zUuid.nullable(),
    /** Scope package title the quote was against, for disambiguation. */
    packageTitle: z.string().max(300).nullable(),
    alreadyAssigned: z.boolean(),
});
// ----------------------------------------------------------------- work items
export const ProjectWorkItemSchema = z.object({
    id: zUuid,
    assignmentId: zUuid,
    position: z.number().int().min(0),
    /** What the work is, e.g. "Primer". */
    title: z.string().min(1).max(300),
    /** Where it is, e.g. "Master bedroom". */
    area: z.string().max(200),
    detail: z.string().max(4000),
    status: ProjectWorkItemStatusSchema,
    completedAt: zIsoDateTime.nullable(),
    /** Company granularity only — user identities never cross tenants. */
    completedByCompanyId: zUuid.nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateProjectWorkItemRequestSchema = z
    .object({
    title: z.string().min(1).max(300),
    area: z.string().max(200).optional(),
    detail: z.string().max(4000).optional(),
    position: z.number().int().min(0).optional(),
})
    .strict();
export const BulkCreateProjectWorkItemsRequestSchema = z
    .object({ items: z.array(CreateProjectWorkItemRequestSchema).min(1).max(200) })
    .strict();
/** Contractor-only: wording and ordering. Requires `projects:manage`. */
export const UpdateProjectWorkItemRequestSchema = z
    .object({
    title: z.string().min(1).max(300).optional(),
    area: z.string().max(200).optional(),
    detail: z.string().max(4000).optional(),
    position: z.number().int().min(0).optional(),
})
    .strict();
/**
 * Checking a box. Split from the update above on purpose: this needs only
 * `work-logs:write`, so a `field` user can record what happened without
 * holding the rights to reword the work.
 */
export const SetWorkItemStatusRequestSchema = z
    .object({
    status: ProjectWorkItemStatusSchema,
    note: z.string().max(4000).optional(),
    /** Retry key — this write appends an immutable log row. */
    clientRequestId: z.string().min(8).max(64).optional(),
})
    .strict();
// ------------------------------------------------------------------ work logs
export const ProjectWorkLogSchema = z.object({
    id: zUuid,
    projectId: zUuid,
    assignmentId: zUuid,
    workItemId: zUuid.nullable(),
    workItemTitle: z.string().max(300).nullable(),
    authorCompanyId: zUuid,
    authorCompanyName: z.string().max(200).nullable(),
    kind: ProjectWorkLogKindSchema,
    body: z.string().max(8000),
    /** The day the WORK happened; may precede `createdAt`. */
    loggedOn: zDateOnly,
    /** Set on a `correction`: the earlier entry this supersedes. */
    correctsLogId: zUuid.nullable(),
    /** Derived: a later correction pointing at this entry, if any. */
    supersededByLogId: zUuid.nullable(),
    /** Attached field photo (0021). Optional: older producers omit it. */
    fileId: zUuid.nullable().optional(),
    createdAt: zIsoDateTime,
});
export const CreateProjectWorkLogRequestSchema = z
    .object({
    body: z.string().min(1).max(8000),
    kind: ProjectWorkLogAuthorableKindSchema.optional(),
    workItemId: zUuid.nullable().optional(),
    loggedOn: zDateOnly.optional(),
    /** Supersede your own earlier entry on this assignment. */
    correctsLogId: zUuid.nullable().optional(),
    clientRequestId: z.string().min(8).max(64).optional(),
    /** A `work_photo` file THIS company uploaded (DB refuses foreign files). */
    fileId: zUuid.optional(),
})
    .strict();
export const ProjectLogQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    assignmentId: zUuid.optional(),
});
// ------------------------------------------------------------ detail response
/** An assignment with its checklist and its most recent entries. */
export const ProjectAssignmentDetailSchema = z.object({
    assignment: ProjectAssignmentSchema,
    workItems: z.array(ProjectWorkItemSchema),
    recentLog: z.array(ProjectWorkLogSchema),
});
export const ProjectDetailResponseSchema = z.object({
    project: ProjectSchema,
    progress: ProjectProgressSchema,
    assignments: z.array(ProjectAssignmentDetailSchema),
});
export const ProjectProgressResponseSchema = z.object({
    projectId: zUuid,
    progress: ProjectProgressSchema,
    assignments: z.array(z.object({
        assignmentId: zUuid,
        companyName: z.string().max(200).nullable(),
        isOwnCrew: z.boolean(),
        status: ProjectAssignmentStatusSchema,
        progress: ProgressSummarySchema,
    })),
});
// ===========================================================================
// SUB-FACING SHAPES
//
// Declared literally. Do NOT derive these from the contractor shapes above,
// and do NOT add a key here without deciding, deliberately, that a
// subcontractor may see it. There is no client, no estimate, no price, no
// other subcontractor, and no user identity anywhere below.
// ===========================================================================
export const SubProjectWorkItemSchema = z
    .object({
    id: zUuid,
    position: z.number().int().min(0),
    title: z.string().min(1).max(300),
    area: z.string().max(200),
    detail: z.string().max(4000),
    status: ProjectWorkItemStatusSchema,
    completedAt: zIsoDateTime.nullable(),
})
    .strict();
/**
 * A log entry on the sub's own thread. Only two parties can ever appear on it,
 * so the author is a label rather than an id — there is no company id to leak.
 */
export const SubProjectWorkLogSchema = z
    .object({
    id: zUuid,
    workItemId: zUuid.nullable(),
    workItemTitle: z.string().max(300).nullable(),
    kind: ProjectWorkLogKindSchema,
    body: z.string().max(8000),
    loggedOn: zDateOnly,
    author: z.enum(['me', 'contractor']),
    correctsLogId: zUuid.nullable(),
    supersededByLogId: zUuid.nullable(),
    /** Attached field photo (0021). Optional: older producers omit it. */
    fileId: zUuid.nullable().optional(),
    createdAt: zIsoDateTime,
})
    .strict();
/** Inbox row: one per project this company is working on. */
export const SubProjectSummarySchema = z
    .object({
    projectId: zUuid,
    projectName: z.string().min(1).max(300),
    siteLabel: z.string().max(400),
    contractorName: z.string().max(200),
    tradeLabel: z.string().max(200),
    projectStatus: ProjectStatusSchema,
    assignmentStatus: ProjectAssignmentStatusSchema,
    progress: ProgressSummarySchema,
    lastActivityAt: zIsoDateTime.nullable(),
})
    .strict();
export const SubProjectDetailSchema = z
    .object({
    projectId: zUuid,
    projectName: z.string().min(1).max(300),
    siteLabel: z.string().max(400),
    contractorName: z.string().max(200),
    tradeLabel: z.string().max(200),
    scopeSummary: z.string().max(50000),
    projectStatus: ProjectStatusSchema,
    assignmentStatus: ProjectAssignmentStatusSchema,
    submittedAt: zIsoDateTime.nullable(),
    acceptedAt: zIsoDateTime.nullable(),
    progress: ProgressSummarySchema,
    workItems: z.array(SubProjectWorkItemSchema),
    log: z.array(SubProjectWorkLogSchema),
})
    .strict();
/**
 * A sub may only check a box. There is deliberately no `title`/`area` key —
 * the shape refuses a reword before the database has to.
 */
export const UpdateSubWorkItemRequestSchema = z
    .object({
    status: ProjectWorkItemStatusSchema,
    note: z.string().max(4000).optional(),
    clientRequestId: z.string().min(8).max(64).optional(),
})
    .strict();
export const CreateSubWorkLogRequestSchema = z
    .object({
    body: z.string().min(1).max(8000),
    kind: ProjectWorkLogAuthorableKindSchema.optional(),
    workItemId: zUuid.nullable().optional(),
    /** Set when the entry also finishes the referenced item. */
    completesItem: z.boolean().optional(),
    loggedOn: zDateOnly.optional(),
    correctsLogId: zUuid.nullable().optional(),
    clientRequestId: z.string().min(8).max(64).optional(),
    /** A `work_photo` file THIS company uploaded (DB refuses foreign files). */
    fileId: zUuid.optional(),
})
    .strict();
/**
 * The field is the authority on whether the job is done, so open items do not
 * block the declaration — but they do make the note mandatory, so a contractor
 * never sees "complete" over a half-finished checklist with no explanation.
 */
export const CompleteSubPortionRequestSchema = z
    .object({ note: z.string().max(4000).optional() })
    .strict();
export const ReopenSubPortionRequestSchema = z
    .object({ note: z.string().max(4000).optional() })
    .strict();
//# sourceMappingURL=projects.js.map